// src/store/relicVault/actions.js
//
// Every write goes to the backend first and the state follows what it returns.
// The server owns prices, the store rotation and the refresh allowance, so a
// purchase sends only which relic or guild was tapped; the response carries the
// new balance and the new ownership, which is what lands in the store.

import {
  SET_CATALOG_STATUS,
  SET_CATALOG,
  SET_VAULT_STATUS,
  SET_RELIC_VAULT_STATE,
  SET_OWNED_RELICS,
  SET_GUARDIAN_STATE,
  SET_STORE_SLOTS,
  SET_BADGE_CATEGORIES,
  SET_NEW_BADGES,
  RESET_RELIC_VAULT,
} from './actionTypes';
import * as api from 'utils/relicVault/collectionApi';

export const setCatalogStatus = (status, error = null) => ({
  type: SET_CATALOG_STATUS,
  payload: { status, error },
});

export const setCatalog = payload => ({ type: SET_CATALOG, payload });

export const setVaultStatus = (status, error = null) => ({
  type: SET_VAULT_STATUS,
  payload: { status, error },
});

export const setRelicVaultState = payload => ({
  type: SET_RELIC_VAULT_STATE,
  payload,
});

export const setOwnedRelics = ids => ({ type: SET_OWNED_RELICS, payload: ids });

export const setGuardianState = payload => ({
  type: SET_GUARDIAN_STATE,
  payload,
});

export const setStoreSlots = payload => ({ type: SET_STORE_SLOTS, payload });

export const setBadgeCategories = categories => ({
  type: SET_BADGE_CATEGORIES,
  payload: categories,
});

export const setNewlyEarnedBadges = ids => ({ type: SET_NEW_BADGES, payload: ids });

/** Announced already; stop announcing them. */
export const clearNewlyEarnedBadges = () => ({ type: SET_NEW_BADGES, payload: [] });

export const resetRelicVault = () => ({ type: RESET_RELIC_VAULT });

const failure = (error, fallback) => ({
  success: false,
  error: error || fallback,
});

/**
 * The catalogs. Fetched once per session: they are the same for every learner
 * and the server caches them, so re-opening the store costs nothing.
 */
export const loadCatalog =
  ({ token } = {}) =>
  async (dispatch, getState) => {
    const status = getState().relicVault.catalogStatus;
    if (status === 'loading' || status === 'ready') return { success: true };

    dispatch(setCatalogStatus('loading'));
    try {
      dispatch(setCatalog(await api.fetchCatalog({ token })));
      return { success: true };
    } catch (err) {
      dispatch(setCatalogStatus('error', err?.message || 'Could not load the collection.'));
      return failure(err?.message, 'Could not load the collection.');
    }
  };

/**
 * What this learner owns, and the five relics on offer.
 *
 * Badges are awarded by the server while the learner plays, so each read is
 * also how the app finds out about one. The first read is only a baseline -
 * everything already earned is not news - and later reads report what is new.
 */
export const hydrateRelicVault =
  ({ token } = {}) =>
  async (dispatch, getState) => {
    if (!token) return failure(null, 'Not signed in.');
    dispatch(setVaultStatus('loading'));
    try {
      const before = getState().relicVault;
      const { store, ...owned } = await api.fetchVaultState({ token });

      const earned = before.badgesBaselined
        ? owned.ownedBadgeIds.filter(id => !before.ownedBadgeIds.includes(id))
        : [];
      dispatch(setRelicVaultState({ ...owned, badgesBaselined: true }));
      if (store) dispatch(setStoreSlots(store));

      if (earned.length) {
        // The announcement names the badge, so make sure the catalog is there
        // to name it. Badges alone, not the 675-relic catalog.
        if (!before.badgeCategories.length) {
          try {
            dispatch(setBadgeCategories(await api.fetchBadgeCatalog({ token })));
          } catch (_) {
            // No names is still worth announcing.
          }
        }
        dispatch(setNewlyEarnedBadges(earned));
      }
      return { success: true };
    } catch (err) {
      dispatch(setVaultStatus('error', err?.message || 'Could not load your vault.'));
      return failure(err?.message, 'Could not load your vault.');
    }
  };

/**
 * Buy a relic. It must be one of the five on offer - the server refuses any
 * other, so the client never has to decide what is purchasable.
 */
export const buyRelic =
  ({ token, relicId }) =>
  async dispatch => {
    try {
      const resp = await api.purchaseRelic({ token, relicId });
      dispatch(setOwnedRelics(resp?.ownedRelicIds));
      return { success: true, newBalance: resp?.newBalance };
    } catch (err) {
      return failure(err?.message, 'Purchase failed.');
    }
  };

/** Sell an owned relic back. The server decides the refund. */
export const sellRelic =
  ({ token, relicId }) =>
  async dispatch => {
    try {
      const resp = await api.sellRelic({ token, relicId });
      dispatch(setOwnedRelics(resp?.ownedRelicIds));
      return { success: true, newBalance: resp?.newBalance, refund: resp?.refund };
    } catch (err) {
      return failure(err?.message, 'Sale failed.');
    }
  };

/** Pay for the next five relics. The server enforces the daily allowance. */
export const refreshStoreSlots =
  ({ token }) =>
  async dispatch => {
    try {
      const resp = await api.refreshStore({ token });
      dispatch(setStoreSlots(resp));
      return { success: true, newBalance: resp?.newBalance };
    } catch (err) {
      return failure(err?.message, 'Refresh failed.');
    }
  };

/** Buy a guardian. The server equips it as part of the purchase. */
export const buyCompanion =
  ({ token, guildId }) =>
  async dispatch => {
    try {
      const resp = await api.purchaseGuardian({ token, guildId });
      dispatch(setGuardianState(resp));
      return { success: true, newBalance: resp?.newBalance };
    } catch (err) {
      return failure(err?.message, 'Purchase failed.');
    }
  };

/** Equip an owned guardian, or "none" to send it away. No coins move. */
export const equipCompanion =
  ({ token, guildId }) =>
  async dispatch => {
    try {
      dispatch(setGuardianState(await api.equipGuardian({ token, guildId })));
      return { success: true };
    } catch (err) {
      return failure(err?.message, 'Could not equip that guardian.');
    }
  };
